"use client";

import { useState, useEffect, useRef } from "react";
import Image from "next/image";
import { partnerLogo } from "@/utils/partnerLogo";

function LogoCard({ initialLogo, initialName }) {
  const [currentLogo, setCurrentLogo] = useState({
    img: initialLogo,
    altText: initialName,
  });
  const [nextLogo, setNextLogo] = useState({
    img: initialLogo,
    altText: initialName,
  });
  const [isTransitioning, setIsTransitioning] = useState(false);

  // keep latest currentLogo in a ref so timers don't use stale state
  const currentLogoRef = useRef(currentLogo);
  useEffect(() => {
    currentLogoRef.current = currentLogo;
  }, [currentLogo]);

  useEffect(() => {
    // Add initial random delay so cards don't all change at once (spread over 20 seconds)
    const initialDelay = Math.random() * 20000;

    let startTimeoutId;
    let showTimeoutId;
    let transitionTimeoutId;
    let nextCycleTimeoutId;

    const pickDifferentLogo = () => {
      const currentImg = currentLogoRef.current.img;
      if (!partnerLogo?.length) return { img: initialLogo, altText: initialName };

      let picked = partnerLogo[Math.floor(Math.random() * partnerLogo.length)];
      let attempts = 0;

      while (picked.img === currentImg && attempts < 20) {
        picked = partnerLogo[Math.floor(Math.random() * partnerLogo.length)];
        attempts += 1;
      }
      return picked;
    };

    const runCycle = () => {
      // show for 20s, then transition to next
      showTimeoutId = setTimeout(() => {
        const picked = pickDifferentLogo();
        setNextLogo(picked);

        // Start slide-up transition
        setIsTransitioning(true);

        // After transition completes (1500ms), update current logo and reset
        transitionTimeoutId = setTimeout(() => {
          setCurrentLogo(picked);
          setIsTransitioning(false);

          // Wait 20 seconds before starting the next cycle
          nextCycleTimeoutId = setTimeout(() => {
            runCycle();
          }, 20000);
        }, 1500);
      }, 20000);
    };

    startTimeoutId = setTimeout(() => {
      runCycle();
    }, initialDelay);

    return () => {
      clearTimeout(startTimeoutId);
      clearTimeout(showTimeoutId);
      clearTimeout(transitionTimeoutId);
      clearTimeout(nextCycleTimeoutId);
    };
  }, [initialLogo, initialName]);

  return (
    <div
      className="bg-white border-[#e2ecf4] border-solid flex items-center justify-center flex-1 relative overflow-hidden"
      style={{
        height: "115px",
        borderWidth: "0.843px",
        minWidth: 0,
      }}
    >
      {/* Current Logo - slides up and out */}
      <div
        className="absolute inset-0 flex items-center justify-center px-6 py-4 transition-all duration-[1500ms] ease-in-out"
        style={{
          opacity: isTransitioning ? 0 : 1,
          transform: isTransitioning ? "translateY(-100%)" : "translateY(0)",
        }}
      >
        <Image
          src={currentLogo.img}
          alt={currentLogo.altText}
          width={220}
          height={120}
          className="object-contain"
          style={{
            mixBlendMode: "luminosity",
            maxWidth: "100%",
            maxHeight: "100%",
            width: "auto",
            height: "auto",
          }}
        />
      </div>

      {/* Next Logo - slides up from bottom */}
      <div
        className="absolute inset-0 flex items-center justify-center px-6 py-4 transition-all duration-[1500ms] ease-in-out"
        style={{
          opacity: isTransitioning ? 1 : 0,
          transform: isTransitioning ? "translateY(0)" : "translateY(100%)",
        }}
      >
        <Image
          src={nextLogo.img}
          alt={nextLogo.altText}
          width={220}
          height={120}
          className="object-contain"
          style={{
            mixBlendMode: "luminosity",
            maxWidth: "100%",
            maxHeight: "100%",
            width: "auto",
            height: "auto",
          }}
        />
      </div>
    </div>
  );
}

export function IndustryCollaborationsAlt() {
  // Generate 30 initial logos (cycling through partnerLogo)
  const initialCards = Array.from({ length: 30 }, (_, i) => ({
    cardIndex: i,
    ...partnerLogo[i % partnerLogo.length],
  }));

  return (
    <section className="w-full bg-[#081e2d] py-20">
      <div className="w-full">
        {/* Header */}
        <div className="text-center mb-16 px-6">
          <h2
            className="mb-4 text-white"
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "clamp(2rem, 5vw, 3rem)",
              fontWeight: 700,
            }}
          >
            Our Industry Collaborations
          </h2>
          <p
            className="max-w-3xl mx-auto text-gray-300"
            style={{
              fontFamily: "'Source Sans Pro', sans-serif",
              fontSize: "clamp(1rem, 2vw, 1.125rem)",
              fontWeight: 400,
              lineHeight: 1.6,
            }}
          >
            Trusted by leading organizations across industries
          </p>
        </div>

        {/* Logo Grid */}
        <div className="flex flex-col gap-px overflow-hidden">
          {/* Row 1 */}
          <div className="flex gap-px">
            {initialCards.slice(0, 6).map((card) => (
              <LogoCard
                key={card.cardIndex}
                initialLogo={card.img}
                initialName={card.altText}
              />
            ))}
          </div>

          {/* Row 2 */}
          <div className="flex gap-px">
            {initialCards.slice(6, 12).map((card) => (
              <LogoCard
                key={card.cardIndex}
                initialLogo={card.img}
                initialName={card.altText}
              />
            ))}
          </div>

          {/* Row 3 */}
          <div className="flex gap-px">
            {initialCards.slice(12, 18).map((card) => (
              <LogoCard
                key={card.cardIndex}
                initialLogo={card.img}
                initialName={card.altText}
              />
            ))}
          </div>

          {/* Row 4 */}
          <div className="flex gap-px">
            {initialCards.slice(18, 24).map((card) => (
              <LogoCard
                key={card.cardIndex}
                initialLogo={card.img}
                initialName={card.altText}
              />
            ))}
          </div>

          {/* Row 5 */}
          <div className="flex gap-px">
            {initialCards.slice(24, 30).map((card) => (
              <LogoCard
                key={card.cardIndex}
                initialLogo={card.img}
                initialName={card.altText}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
